import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, ArrowRight, X, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * GotOurWhatsAppBanner
 * --------------------
 * Sits below the hero on the public landing page. Targets pros who tapped
 * the static URL button in our WhatsApp outreach and landed on `/`.
 *
 * Flow:
 *   1. Banner CTA → opens modal
 *   2. Modal asks for the phone number we WhatsApp'd
 *   3. POST /api/pros/lookup-claim → returns place_id if matched
 *   4. Navigate to /claim/:place_id (which already exists and has the 14-day
 *      Verified Partner trial activation flow)
 *
 * If no match, we still offer a "sign up free" CTA pointing to /login
 * (their account will pick up the next time outreach matches their phone).
 */
export const GotOurWhatsAppBanner = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!phone.trim()) {
      toast.error('Enter the phone number we messaged');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pros/lookup-claim`, { phone });
      if (res.data?.found && res.data?.place_id) {
        if (res.data.already_claimed) {
          toast.info('This listing is already claimed — sign in to manage it');
          navigate('/login');
        } else {
          toast.success(`Found your listing in ${res.data.city} — claiming now`);
          navigate(res.data.claim_url);
        }
      } else {
        toast.info("Couldn't match — sign up free and we'll find your profile");
        navigate('/login?signup=1');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Lookup failed — try again';
      toast.error(typeof msg === 'string' ? msg : 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Banner strip — narrow, above other content */}
      <section
        className="relative bg-gradient-to-r from-[#0A0A0C] via-[#1A1A1A] to-[#0A0A0C] border-y border-[#D4AF37]/30 overflow-hidden"
        data-testid="got-whatsapp-banner"
      >
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-12 -right-20 w-64 h-64 rounded-full bg-[#25D366] blur-3xl" />
          <div className="absolute -bottom-12 -left-20 w-64 h-64 rounded-full bg-[#D4AF37] blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="hidden sm:flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#25D366]/15 border border-[#25D366]/40">
                <MessageCircle className="h-5 w-5 text-[#25D366]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] font-bold mb-0.5">
                  Got our WhatsApp?
                </p>
                <p className="text-sm sm:text-base text-white/90 leading-snug">
                  Claim your free <span className="text-[#D4AF37] font-semibold">Verified Partner</span> profile in 60 seconds —
                  <span className="hidden sm:inline"> 14-day trial, no card.</span>
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="got-whatsapp-claim-cta"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0C] px-5 py-2.5 text-xs font-bold tracking-[0.15em] uppercase hover:bg-white transition-colors whitespace-nowrap"
            >
              Claim My Profile <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </section>

      {/* Lookup modal */}
      {open && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={(e) => e.target === e.currentTarget && setOpen(false)}
          data-testid="got-whatsapp-modal"
        >
          <div className="bg-white max-w-md w-full overflow-hidden shadow-2xl">
            <div className="relative bg-gradient-to-br from-[#0A0A0C] to-[#1A1A1A] px-6 py-5">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 text-white/50 hover:text-white"
                aria-label="Close"
                data-testid="got-whatsapp-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] font-bold mb-1">
                Verified Partner Claim
              </p>
              <h3 className="font-['Playfair_Display'] text-2xl text-white leading-tight">
                Enter the phone we <span className="text-[#D4AF37]">WhatsApp'd</span>
              </h3>
              <p className="text-white/60 text-sm mt-2">
                We'll deep-link you to your pre-filled claim page — name, city, category all set.
              </p>
            </div>

            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase font-semibold text-[#1A1A1A]/70 mb-2">
                  WhatsApp Number
                </label>
                <input
                  type="tel"
                  autoFocus
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+44 7… or +91 9… or just 10 digits"
                  className="w-full border-2 border-slate-200 px-4 py-3 text-base focus:border-[#D4AF37] focus:outline-none"
                  data-testid="got-whatsapp-phone-input"
                />
                <p className="text-xs text-slate-400 mt-2">
                  Country code optional — we'll find it either way.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="got-whatsapp-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0C] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0C] px-4 py-3.5 text-xs font-bold tracking-[0.18em] uppercase transition-colors disabled:opacity-60"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Looking up…</>
                ) : (
                  <><CheckCircle2 className="h-4 w-4" /> Find My Listing</>
                )}
              </button>

              <p className="text-[11px] text-slate-400 text-center pt-2 leading-relaxed">
                Privacy: phone is used only to match our outreach list.
                Never shared, never sold.
              </p>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default GotOurWhatsAppBanner;
